import { Danger, IconProps, InfoCircle, TickSquare } from 'iconsax-react';
import { AriaAttributes, AriaRole, PropsWithChildren, ReactNode } from 'react';

import classNames from '@/helpers/classNames';

import styles from './GeneralMessage.module.scss';

export type LabelIconType = 'info' | 'error' | 'success' | 'warning';

interface GeneralMessageProps extends PropsWithChildren, AriaAttributes {
  type?: LabelIconType;
  header?: string;
  message?: string;
  size?: 'main' | 'small';
  iconSize?: number;
  className?: string;
  iconClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  messageClassName?: string;
  fullWidth?: boolean;
  role?: AriaRole;
  customIcon?: ReactNode;
  rightColumn?: ReactNode;
  columnLayout?: boolean;
}

function LabelIcon({ type, ...props }: { type?: LabelIconType } & IconProps) {
  switch (type) {
    case 'error':
      return <Danger {...props} />;
    case 'success':
      return <TickSquare {...props} />;
    default:
      return <InfoCircle {...props} />;
  }
}

export default function GeneralMessage(props: GeneralMessageProps) {
  const {
    header,
    message,
    className,
    contentClassName,
    iconClassName,
    headerClassName,
    messageClassName,
    size = '',
    type = 'info',
    iconSize = size === 'small' ? 20 : 24,
    children,
    fullWidth,
    role,
    customIcon,
    rightColumn,
    columnLayout = false,
    ...ariaAttributes
  } = props;
  const containerClassName = classNames(
    [styles.container, styles[type], styles[size], className],
    {
      [styles.fullWidth]: fullWidth,
    }
  );

  const messageContainerClassName = classNames([styles.messageContainer], {
    [styles.columnLayout]: columnLayout,
  });

  return (
    <div className={containerClassName}>
      <div className={messageContainerClassName}>
        {customIcon ? (
          <span className={classNames([styles.icon, iconClassName])}>
            {customIcon}
          </span>
        ) : (
          <LabelIcon
            type={type}
            variant="Linear"
            color="currentColor"
            size={iconSize}
            className={classNames([styles.icon, iconClassName])}
          />
        )}
        <div
          className={classNames([styles.content, contentClassName])}
          role={role}
          {...ariaAttributes}
        >
          {header && (
            <h3 className={classNames([styles.title, headerClassName])}>
              {header}
            </h3>
          )}
          {message && (
            <p className={classNames([styles.description, messageClassName])}>
              {message}
            </p>
          )}
          {children}
        </div>
      </div>
      {rightColumn}
    </div>
  );
}
